import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { AxiosApiError } from '@/types/apiError';
import { useMutation, useQueryClient, InfiniteData } from '@tanstack/react-query';
import { REPORT_GROUPS_QUERY_KEY } from './useInfiniteReportGroups';
import { ListReportGroupsResponse, ReportGroup } from '@/api/generatedApi';
import { AxiosResponse } from 'axios';

export const useUpdateReportGroupName = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: string) => void;
} = {}) => {
  const queryClient = useQueryClient();
  const { getTenantId } = useTenants();

  const tenantId = getTenantId();

  const { mutate, isPending } = useMutation({
    mutationFn: ({ reportId, groupId, name }: { reportId: string; groupId: string; name: string }) =>
      api.reports.updateReportGroupName(tenantId, reportId, groupId, { name }),
    onMutate: async ({ groupId, name }) => {
      const previousData = queryClient.getQueriesData({ queryKey: [REPORT_GROUPS_QUERY_KEY] });

      queryClient.setQueriesData<InfiniteData<AxiosResponse<ListReportGroupsResponse>>>(
        { queryKey: [REPORT_GROUPS_QUERY_KEY] },
        old => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map(page => ({
              ...page,
              data: {
                ...page.data,
                groups: page.data.groups.map((group: ReportGroup) =>
                  group.id === groupId ? { ...group, name } : group
                ),
              },
            })),
          };
        }
      );

      return { previousData };
    },
    onError: (error: AxiosApiError, _variables, context) => {
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      onError?.(error.message || 'Failed to update group name');
    },
    onSuccess: () => {
      onSuccess?.();
    },
  });

  return {
    mutate,
    isPending,
  };
};
